import React, { useEffect, useState, useRef } from 'react';

const progressSteps = [
  {
    header: 'Learning about [Brand]',
    steps: [
      'Gathering historic public interest information.',
      'Collecting market-by-market public interest.',
      'Calculating brands of comparable interest.',
      'Reviewing media mentions.',
      'Evaluating social accounts ... interesting.',
      { text: 'Finalizing [Brand] analysis.', isLast: true }
    ]
  },
  {
    header: 'Learning about [Partner]',
    steps: [
      'Browsing the internet.',
      'Classifying [Partner].',
      'Gathering historic public interest.',
      { text: 'Evaluating social accounts.', isLast: true }
    ]
  },
  {
    header:
      'Leveraging our statistical model of past fusions to generate forecasts.',
    steps: [
      'Determining best public interest forecast method.',
      'ARIMA (0,1,0).',
      'Generating timeseries charts.',
      'Generating geographies of interest.',
      'Calculating ignition thrust.',
      { text: 'Informing fusion assistant.', isLast: true }
    ]
  },
  {
    header: 'Generating social content',
    steps: ['Generating ideas.', { text: 'Generating images.', isLast: true }]
  },
  {
    header: 'Arranging Fusion Board',
    steps: [{ text: 'Finalizing details.', isLast: true }]
  }
];

const ProgressBar = ({ brand, partner, isActive, onComplete, hasLoaded }) => {
  const [step, setStep] = useState(0);
  const [nextStep, setNextStep] = useState(0);
  const [headerFading, setHeaderFading] = useState(false);
  const [stepFading, setStepFading] = useState(false);
  const [percentageFading, setPercentageFading] = useState(false);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const intervalRef = useRef(null);

  const totalSteps = progressSteps.flatMap((section) => section.steps).length;

  const getCurrentSection = (currentStep) => {
    let currentStepIndex = 0;
    for (const section of progressSteps) {
      if (currentStep < currentStepIndex + section.steps.length) {
        return section.header
          .replace('[Brand]', brand)
          .replace('[Partner]', partner);
      }
      currentStepIndex += section.steps.length;
    }
    return '';
  };

  const getCurrentStep = (currentStep) => {
    let currentStepIndex = 0;
    for (const section of progressSteps) {
      if (currentStep < currentStepIndex + section.steps.length) {
        const currentStepItem = section.steps[currentStep - currentStepIndex];
        const stepText =
          typeof currentStepItem === 'string'
            ? currentStepItem
            : currentStepItem.text;

        return stepText.replace('[Brand]', brand).replace('[Partner]', partner);
      }
      currentStepIndex += section.steps.length;
    }
    return '';
  };

  const handleStepUpdate = (nextStepValue) => {
    let currentStepIndex = 0;
    let isNextHeader = false;

    for (const section of progressSteps) {
      for (let i = 0; i < section.steps.length; i++) {
        const currentStepItem = section.steps[i];
        if (currentStepIndex === nextStepValue - 1 && currentStepItem?.isLast) {
          isNextHeader = true;
        }
        currentStepIndex++;
      }
    }

    setStepFading(true);
    setPercentageFading(true);
    if (isNextHeader) setHeaderFading(true);

    setTimeout(() => {
      setNextStep(nextStepValue);
      setStepFading(false);
      setPercentageFading(false);
      if (isNextHeader) setHeaderFading(false);
    }, 300);
  };

  useEffect(() => {
    if (!isActive) return;

    setStep(0);
    setNextStep(0);
    setIsFullWidth(true);

    intervalRef.current = setInterval(() => {
      setStep((prevStep) => {
        const nextStepValue = prevStep + 1;

        // Pause at the last step until fusion is loaded
        if (nextStepValue >= totalSteps - 1 && !hasLoaded) {
          return prevStep;
        }

        if (nextStepValue < totalSteps) {
          handleStepUpdate(nextStepValue);
          return nextStepValue;
        } else {
          clearInterval(intervalRef.current);
          onComplete && onComplete();
          return prevStep;
        }
      });
    }, 3250);

    return () => clearInterval(intervalRef.current);
  }, [isActive, hasLoaded]);

  if (!isActive) return null;

  let progressPercentage;
  if (step >= totalSteps - 2 && !hasLoaded) {
    progressPercentage = 99;
  } else {
    progressPercentage = Math.round((step / (totalSteps - 1)) * 100);
  }

  const progressWidth = `${progressPercentage}%`;

  return (
    <div className="progress-container">
      <div className={`progress-bar ${isFullWidth ? 'full-width' : ''}`}>
        <div className="progress-fill" style={{ width: progressWidth }}></div>
        <p
          className={`progress-percentage ${
            percentageFading ? 'fade-out-percentage' : 'fade-in-percentage'
          }`}
        >
          {progressPercentage}%
        </p>
      </div>
      <div className="progress-info">
        <p
          className={`progress-header ${headerFading ? 'fade-out' : 'fade-in'}`}
        >
          {getCurrentSection(nextStep)}
        </p>
        <p className={`loading-message ${stepFading ? 'fade-out' : 'fade-in'}`}>
          {getCurrentStep(nextStep)}
        </p>
      </div>
    </div>
  );
};

export default ProgressBar;
